/**
 * ScannerSystem - Progressive specimen analysis
 * From PRD Section 2.3.2 and 6.2.2
 */

import Phaser from 'phaser';
import type { ScanResult, EvidenceCard, Vector2 } from '@/types';
import { LifeFunction, EvidenceType, Classification } from '@/types';

export interface ScannerConfig {
  /** Time in ms for a full scan */
  scanDuration?: number;
  /** Minimum distance to specimen to scan */
  scanRange?: number;
  /** Progress thresholds for revealing data */
  progressThresholds?: ScanProgressThresholds;
}

export interface ScanProgressThresholds {
  cellCount: number;    // 20%
  cellType: number;     // 40%
  isLiving: number;     // 60%
  functions: number;    // 80%
  complete: number;     // 100%
}

export interface Specimen {
  id: string;
  position: Vector2;
  isLiving: boolean;
  cellCount: number | 'none' | 'single' | 'multiple';
  cellType: 'prokaryotic' | 'eukaryotic' | 'eukaryotic-plant' | 'none';
  lifeFunctions: LifeFunction[];
  bounds?: Phaser.Geom.Rectangle;
}

export interface PartialScanResult {
  cellCount?: ScanResult['cellCount'];
  cellType?: ScanResult['cellType'];
  isLiving?: boolean;
  detectedFunctions?: LifeFunction[];
  confidence: number;
}

export interface ScanProgressEvent {
  percent: number;
  partialResult: PartialScanResult;
  specimen: Specimen;
}

export interface ScanCompleteEvent {
  specimen: Specimen;
  result: ScanResult;
  evidence: EvidenceCard[];
}

/**
 * ScannerSystem handles progressive specimen analysis
 * Hold pointer over specimen to build confidence and reveal data
 */
export class ScannerSystem {
  private scene: Phaser.Scene;
  private config: Required<ScannerConfig>;

  private isActive = false;
  private currentSpecimen: Specimen | null = null;
  private scanProgress = 0;
  private specimens: Map<string, Specimen> = new Map();

  /** Scan timer */
  private scanTimer: Phaser.Time.TimerEvent | null = null;

  /** Default progress thresholds */
  private static readonly DEFAULT_THRESHOLDS: ScanProgressThresholds = {
    cellCount: 0.2,    // 20%
    cellType: 0.4,     // 40%
    isLiving: 0.6,     // 60%
    functions: 0.8,    // 80%
    complete: 1.0,     // 100%
  };

  constructor(scene: Phaser.Scene, config: ScannerConfig = {}) {
    this.scene = scene;
    this.config = {
      scanDuration: config.scanDuration ?? 3000,
      scanRange: config.scanRange ?? 100,
      progressThresholds: config.progressThresholds ?? ScannerSystem.DEFAULT_THRESHOLDS,
    };

    this.setupInputHandlers();
  }

  /**
   * Set up input handlers for scanning
   */
  private setupInputHandlers(): void {
    this.scene.input.on('pointermove', this.handlePointerMove, this);
    this.scene.input.on('pointerdown', this.handlePointerDown, this);
    this.scene.input.on('pointerup', this.handlePointerUp, this);
  }

  /**
   * Handle pointer movement for specimen detection
   */
  private handlePointerMove(pointer: Phaser.Input.Pointer): void {
    if (!this.isActive) return;

    const specimen = this.findSpecimenAtPoint(pointer.x, pointer.y);

    if (specimen && specimen !== this.currentSpecimen) {
      // New specimen, reset progress
      this.currentSpecimen = specimen;
      this.scanProgress = 0;
      this.startScanTimer();
    } else if (!specimen && this.currentSpecimen) {
      // Moved off specimen
      this.stopScan();
    }
  }

  /**
   * Handle pointer down (continuous scan while pressed)
   */
  private handlePointerDown(pointer: Phaser.Input.Pointer): void {
    if (!this.isActive) return;

    const specimen = this.findSpecimenAtPoint(pointer.x, pointer.y);
    if (specimen) {
      this.currentSpecimen = specimen;
      this.startScanTimer();
    }
  }

  /**
   * Handle pointer up
   */
  private handlePointerUp(): void {
    // Optionally pause scan when pointer released
    // For now, continue scanning as long as over specimen
  }

  /**
   * Find specimen at a given point
   */
  private findSpecimenAtPoint(x: number, y: number): Specimen | null {
    for (const specimen of this.specimens.values()) {
      if (specimen.bounds) {
        if (specimen.bounds.contains(x, y)) {
          return specimen;
        }
      } else {
        // Use position and scan range
        const dx = x - specimen.position.x;
        const dy = y - specimen.position.y;
        const distance = Math.sqrt(dx * dx + dy * dy);

        if (distance <= this.config.scanRange) {
          return specimen;
        }
      }
    }
    return null;
  }

  /**
   * Start the scan timer
   */
  private startScanTimer(): void {
    this.stopScanTimer();

    const tickInterval = 50; // Update every 50ms
    const progressPerTick = tickInterval / this.config.scanDuration;

    this.scanTimer = this.scene.time.addEvent({
      delay: tickInterval,
      callback: () => this.updateScan(progressPerTick),
      loop: true,
    });
  }

  /**
   * Stop the scan timer
   */
  private stopScanTimer(): void {
    if (this.scanTimer) {
      this.scanTimer.remove();
      this.scanTimer = null;
    }
  }

  /**
   * Update scan progress
   */
  private updateScan(progressDelta: number): void {
    if (!this.currentSpecimen) return;

    this.scanProgress = Math.min(1, this.scanProgress + progressDelta);

    const partialResult = this.buildPartialResult(this.currentSpecimen, this.scanProgress);

    // Emit progress event
    const progressEvent: ScanProgressEvent = {
      percent: this.scanProgress * 100,
      partialResult,
      specimen: this.currentSpecimen,
    };
    this.scene.events.emit('scanner:progress', progressEvent);

    // Check for completion
    if (this.scanProgress >= this.config.progressThresholds.complete) {
      this.completeScan();
    }
  }

  /**
   * Build partial result based on current progress
   */
  private buildPartialResult(specimen: Specimen, progress: number): PartialScanResult {
    const thresholds = this.config.progressThresholds;
    const result: PartialScanResult = {
      confidence: progress,
    };

    if (progress >= thresholds.cellCount) {
      result.cellCount = specimen.cellCount;
    }

    if (progress >= thresholds.cellType) {
      result.cellType = specimen.cellType;
    }

    if (progress >= thresholds.isLiving) {
      result.isLiving = specimen.isLiving;
    }

    if (progress >= thresholds.functions) {
      result.detectedFunctions = [...specimen.lifeFunctions];
    }

    return result;
  }

  /**
   * Complete the scan and generate evidence
   */
  private completeScan(): void {
    if (!this.currentSpecimen) return;

    const specimen = this.currentSpecimen;

    // Build complete result
    const result: ScanResult = {
      isLiving: specimen.isLiving,
      cellCount: specimen.cellCount,
      cellType: specimen.cellType,
      detectedFunctions: [...specimen.lifeFunctions],
      confidence: 1,
    };

    // Generate evidence cards
    const evidence = this.generateEvidence(specimen, result);

    // Emit events
    const completeEvent: ScanCompleteEvent = {
      specimen,
      result,
      evidence,
    };

    this.scene.events.emit('scanner:complete', completeEvent);

    // Emit evidence collected events
    for (const card of evidence) {
      this.scene.events.emit('evidence:collected', card);
    }

    // Reset scan state
    this.stopScan();
  }

  /**
   * Generate evidence cards from scan result
   */
  private generateEvidence(specimen: Specimen, result: ScanResult): EvidenceCard[] {
    const evidence: EvidenceCard[] = [];

    // Living/Non-living evidence
    if (result.isLiving) {
      evidence.push({
        id: `${specimen.id}_living`,
        type: EvidenceType.STRUCTURAL,
        description: 'Contains cell membrane and internal structures',
        supportsClassification: [Classification.LIVING],
        contradictsClassification: [Classification.NON_LIVING],
      });
    } else {
      evidence.push({
        id: `${specimen.id}_nonliving`,
        type: EvidenceType.STRUCTURAL,
        description: 'No cellular organization detected',
        supportsClassification: [Classification.NON_LIVING],
        contradictsClassification: [Classification.LIVING],
      });
    }

    // Cell count evidence
    if (result.cellCount === 'single' || result.cellCount === 1) {
      evidence.push({
        id: `${specimen.id}_single_cell`,
        type: EvidenceType.STRUCTURAL,
        description: 'Composed of a single cell',
        supportsClassification: [Classification.UNICELLULAR],
        contradictsClassification: [Classification.MULTICELLULAR],
      });
    } else if (result.cellCount === 'multiple' || (typeof result.cellCount === 'number' && result.cellCount > 1)) {
      evidence.push({
        id: `${specimen.id}_multiple_cells`,
        type: EvidenceType.STRUCTURAL,
        description: 'Composed of multiple cells working together',
        supportsClassification: [Classification.MULTICELLULAR],
        contradictsClassification: [Classification.UNICELLULAR],
      });
    }

    // Cell type evidence
    if (result.cellType === 'prokaryotic') {
      evidence.push({
        id: `${specimen.id}_prokaryotic`,
        type: EvidenceType.COMPOSITIONAL,
        description: 'Prokaryotic cell structure (no membrane-bound nucleus)',
        supportsClassification: [Classification.LIVING, Classification.UNICELLULAR],
        contradictsClassification: [Classification.NON_LIVING],
      });
    } else if (result.cellType === 'eukaryotic' || result.cellType === 'eukaryotic-plant') {
      evidence.push({
        id: `${specimen.id}_eukaryotic`,
        type: EvidenceType.COMPOSITIONAL,
        description: 'Eukaryotic cell structure (membrane-bound nucleus)',
        supportsClassification: [Classification.LIVING],
        contradictsClassification: [Classification.NON_LIVING],
      });
    }

    // Life function evidence
    for (const func of result.detectedFunctions) {
      evidence.push({
        id: `${specimen.id}_function_${func}`,
        type: EvidenceType.FUNCTIONAL,
        description: this.getLifeFunctionDescription(func),
        supportsClassification: [Classification.LIVING],
        contradictsClassification: [Classification.NON_LIVING],
      });
    }

    return evidence;
  }

  /**
   * Get human-readable description for life function
   */
  private getLifeFunctionDescription(func: LifeFunction): string {
    switch (func) {
      case LifeFunction.METABOLISM:
        return 'Performs metabolic processes to obtain energy';
      case LifeFunction.REPRODUCTION:
        return 'Capable of reproduction and cell division';
      case LifeFunction.RESPONSE_TO_STIMULI:
        return 'Responds to environmental stimuli';
      case LifeFunction.HOMEOSTASIS:
        return 'Maintains internal balance (homeostasis)';
      case LifeFunction.GROWTH:
        return 'Shows evidence of growth';
      case LifeFunction.WASTE_REMOVAL:
        return 'Removes waste products';
      case LifeFunction.GAS_EXCHANGE:
        return 'Performs gas exchange';
      default:
        return 'Exhibits life function';
    }
  }

  /**
   * Stop current scan
   */
  private stopScan(): void {
    this.stopScanTimer();
    this.currentSpecimen = null;
    this.scanProgress = 0;
  }

  /**
   * Activate the scanner
   */
  public activate(): void {
    if (this.isActive) return;

    this.isActive = true;
    this.scene.events.emit('scanner:activated');
  }

  /**
   * Deactivate the scanner
   */
  public deactivate(): void {
    if (!this.isActive) return;

    this.stopScan();
    this.isActive = false;
    this.scene.events.emit('scanner:deactivated');
  }

  /**
   * Toggle scanner state
   */
  public toggle(): void {
    if (this.isActive) {
      this.deactivate();
    } else {
      this.activate();
    }
  }

  /**
   * Check if scanner is active
   */
  public isActiveState(): boolean {
    return this.isActive;
  }

  /**
   * Register a specimen for scanning
   */
  public registerSpecimen(specimen: Specimen): void {
    this.specimens.set(specimen.id, specimen);
  }

  /**
   * Unregister a specimen
   */
  public unregisterSpecimen(id: string): void {
    this.specimens.delete(id);
  }

  /**
   * Clear all specimens
   */
  public clearSpecimens(): void {
    this.specimens.clear();
    this.stopScan();
  }

  /**
   * Get current scan progress (0-1)
   */
  public getScanProgress(): number {
    return this.scanProgress;
  }

  /**
   * Get currently scanning specimen
   */
  public getCurrentSpecimen(): Specimen | null {
    return this.currentSpecimen;
  }

  /**
   * Clean up
   */
  public destroy(): void {
    this.deactivate();

    this.scene.input.off('pointermove', this.handlePointerMove, this);
    this.scene.input.off('pointerdown', this.handlePointerDown, this);
    this.scene.input.off('pointerup', this.handlePointerUp, this);

    this.specimens.clear();
  }
}
